/**
 * Store Zustand para gerenciamento de estado de Notificações
 */

import { create } from 'zustand';
import type { 
  Notificacao, 
  NotificacaoCompacta, 
  AdicionarNotificacaoRequest,
  AtualizarNotificacaoRequest,
  ReportarProblemaRequest
} from '@/components/notificacoes/core/models';
import { RemoteNotificacaoRepository } from '@/components/notificacoes/data/remote_notificacao_repository';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';

interface NotificacoesState {
  notificacoes: Notificacao[];
  notificacoesCompactas: NotificacaoCompacta[];
  isLoading: boolean;
  error: string | null;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  
  // Actions
  fetchNotificacoes: (compacto?: boolean, pageNumber?: number, reset?: boolean) => Promise<void>;
  fetchNotificacaoPorId: (id: string, compacto?: boolean) => Promise<Notificacao | NotificacaoCompacta>;
  adicionarNotificacao: (request: AdicionarNotificacaoRequest) => Promise<Notificacao>;
  marcarComoLida: (id: string) => Promise<void>;
  atualizarNotificacao: (id: string, request: AtualizarNotificacaoRequest) => Promise<Notificacao>;
  removerNotificacao: (id: string) => Promise<void>;
  reportarProblema: (request: ReportarProblemaRequest) => Promise<Notificacao>;
  clearError: () => void;
  resetPagination: () => void;
}

const dataSource = new RemoteDataSource(Settings.urlBase);
const repository = new RemoteNotificacaoRepository(dataSource);

export const useNotificacoesStore = create<NotificacoesState>((set, get) => ({
  notificacoes: [],
  notificacoesCompactas: [],
  isLoading: false,
  error: null,
  pageNumber: 1,
  pageSize: 10,
  hasMore: true,
  isLoadingMore: false,

  fetchNotificacoes: async (compacto = false, pageNumber = 1, reset = false) => {
    const { pageSize, notificacoes: existingNotificacoes, notificacoesCompactas: existingNotificacoesCompactas } = get();
    if (reset) {
      set({ isLoading: true, pageNumber: 1, notificacoes: [], notificacoesCompactas: [], error: null });
    } else if (pageNumber === 1) {
      set({ isLoading: true, error: null });
    } else {
      set({ isLoadingMore: true, error: null });
    }
    try {
      const data = await repository.obterNotificacoes(compacto, pageNumber, pageSize);
      const hasMore = data.length === pageSize;
      if (compacto) {
        const mapped = data as NotificacaoCompacta[];
        if (reset || pageNumber === 1) {
          set({ notificacoesCompactas: mapped, pageNumber, hasMore, isLoading: false, isLoadingMore: false });
        } else {
          set({ notificacoesCompactas: [...existingNotificacoesCompactas, ...mapped], pageNumber, hasMore, isLoading: false, isLoadingMore: false });
        }
      } else {
        const mapped = data as Notificacao[];
        if (reset || pageNumber === 1) {
          set({ notificacoes: mapped, pageNumber, hasMore, isLoading: false, isLoadingMore: false });
        } else {
          set({ notificacoes: [...existingNotificacoes, ...mapped], pageNumber, hasMore, isLoading: false, isLoadingMore: false });
        }
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao buscar notificações',
        isLoading: false,
        isLoadingMore: false
      });
    }
  },

  fetchNotificacaoPorId: async (id: string, compacto = false) => {
    set({ isLoading: true, error: null });
    try {
      const notificacao = await repository.obterNotificacaoPorId(id, compacto);
      set({ isLoading: false });
      return notificacao;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao buscar notificação',
        isLoading: false 
      });
      throw error;
    }
  },

  adicionarNotificacao: async (request: AdicionarNotificacaoRequest) => {
    set({ isLoading: true, error: null });
    try {
      const notificacao = await repository.adicionarNotificacao(request);
      set((state) => ({ 
        notificacoes: [...state.notificacoes, notificacao],
        isLoading: false 
      }));
      return notificacao;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao adicionar notificação',
        isLoading: false 
      });
      throw error;
    }
  },

  marcarComoLida: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await repository.marcarComoLida(id);
      set((state) => ({
        notificacoes: state.notificacoes.map((n) =>
          n.id === id ? { ...n, lida: true } : n
        ),
        notificacoesCompactas: state.notificacoesCompactas.map((n) =>
          n.id === id ? { ...n, lida: true } : n
        ),
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao marcar notificação como lida',
        isLoading: false 
      });
      throw error;
    }
  },

  atualizarNotificacao: async (id: string, request: AtualizarNotificacaoRequest) => {
    set({ isLoading: true, error: null });
    try {
      const notificacao = await repository.atualizarNotificacao(id, request);
      set((state) => ({
        notificacoes: state.notificacoes.map((n) =>
          n.id === id ? notificacao : n
        ),
        isLoading: false,
      }));
      return notificacao;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao atualizar notificação',
        isLoading: false 
      });
      throw error;
    }
  },

  removerNotificacao: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await repository.removerNotificacao(id);
      set((state) => ({
        notificacoes: state.notificacoes.filter((n) => n.id !== id),
        notificacoesCompactas: state.notificacoesCompactas.filter((n) => n.id !== id),
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao remover notificação',
        isLoading: false 
      });
      throw error;
    }
  },

  reportarProblema: async (request: ReportarProblemaRequest) => {
    set({ isLoading: true, error: null });
    try {
      const notificacao = await repository.reportarProblema(request);
      set((state) => ({ 
        notificacoes: [...state.notificacoes, notificacao],
        isLoading: false 
      }));
      return notificacao;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao reportar problema',
        isLoading: false 
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  resetPagination: () => {
    set({ pageNumber: 1, hasMore: true, isLoadingMore: false, notificacoes: [], notificacoesCompactas: [] });
  },
}));
